import { useState } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { LanguageSwitcher } from '@/components/shared/LanguageSwitcher';

const HeaderEn = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-sm border-b border-gray-800">
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link to="/en/" className="flex items-center space-x-3">
            <img
              src="/lovable-uploads/SSN_Logo_Horizontal_White.png"
              alt="Sweden Startup Nation"
              className="h-8"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-2">
            {/* Startups */}
            <Link to="/en/startups">
              <button className="nav-link inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-none">
                Startups
              </button>
            </Link>

            {/* Scale-ups */}
            <Link to="/en/scaleups">
              <button className="nav-link inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-none">
                Scale-ups
              </button>
            </Link>

            {/* Startup Journey */}
            <Link to="/en/startup-journey">
              <button className="nav-link inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-none">
                Startup Journey
              </button>
            </Link>

            {/* Reports */}
            <div className="group relative">
              <Link to="/en/reports">
                <button className="nav-link inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-none">
                  Reports
                </button>
              </Link>
            </div>

            {/* Insight Lab Main Menu */}
            <div className="group relative">
              <button className="nav-link inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-none">
                Insight Lab
                <ChevronDown className="ml-1 h-3 w-3" />
              </button>
              <div className="absolute left-0 top-full pt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="w-auto min-w-40 bg-white border border-gray-200 shadow-xl rounded-md p-1 backdrop-blur-md">
                  <Link
                    to="/en/insightlab/what-we-do"
                    className="nav-dropdown-link block px-2 py-1.5 text-sm transition-colors rounded-sm"
                  >
                    What we do
                  </Link>
                  <Link
                    to="/en/insightlab/consultation"
                    className="nav-dropdown-link block px-2 py-1.5 text-sm transition-colors rounded-sm"
                  >
                    Consultation
                  </Link>
                  <Link
                    to="/en/insightlab/research"
                    className="nav-dropdown-link block px-2 py-1.5 text-sm transition-colors rounded-sm"
                  >
                    Research
                  </Link>
                  <Link
                    to="/en/insightlab/contribute"
                    className="nav-dropdown-link block px-2 py-1.5 text-sm transition-colors rounded-sm"
                  >
                    Contribute
                  </Link>
                  <Link
                    to="/en/calendar"
                    className="nav-dropdown-link block px-2 py-1.5 text-sm transition-colors rounded-sm"
                  >
                    Calendar
                  </Link>
                </div>
              </div>
            </div>

            {/* Engage */}
            <div className="group relative">
              <button className="nav-link inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-none">
                Engage
                <ChevronDown className="ml-1 h-3 w-3" />
              </button>
              <div className="absolute left-0 top-full pt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="w-48 bg-white border border-gray-200 shadow-xl rounded-md p-1 backdrop-blur-md">
                  <Link
                    to="/en/engage/startup"
                    className="nav-dropdown-link block px-2 py-1.5 text-sm transition-colors rounded-sm"
                  >
                    For Startups
                  </Link>
                  <Link
                    to="/en/engage/data"
                    className="nav-dropdown-link block px-2 py-1.5 text-sm transition-colors rounded-sm"
                  >
                    For Data Contributors
                  </Link>
                  <Link
                    to="/en/engage/partner"
                    className="nav-dropdown-link block px-2 py-1.5 text-sm transition-colors rounded-sm"
                  >
                    For Partnerships
                  </Link>
                </div>
              </div>
            </div>

            {/* About */}
            <div className="group relative">
              <button className="nav-link inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-none">
                About
                <ChevronDown className="ml-1 h-3 w-3" />
              </button>
              <div className="absolute left-0 top-full pt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="w-48 bg-white border border-gray-200 shadow-xl rounded-md p-1 backdrop-blur-md">
                  <Link
                    to="/en/about"
                    className="nav-dropdown-link block px-2 py-1.5 text-sm transition-colors rounded-sm"
                  >
                    About SSN
                  </Link>
                  <Link
                    to="/en/newsroom"
                    className="nav-dropdown-link block px-2 py-1.5 text-sm transition-colors rounded-sm"
                  >
                    News & Press
                  </Link>
                  <Link
                    to="/en/partners"
                    className="nav-dropdown-link block px-2 py-1.5 text-sm transition-colors rounded-sm"
                  >
                    Partners
                  </Link>
                </div>
              </div>
            </div>

            {/* Language Switcher - Desktop */}
            <div className="ml-4">
              <LanguageSwitcher />
            </div>
          </nav>

          {/* Desktop Actions - Mobile Language Switcher */}
          <div className="flex items-center gap-2 md:hidden">
            <LanguageSwitcher />
            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:text-electric-cyan"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="md:hidden mt-4 pb-4">
            <div className="flex flex-col space-y-2 pt-4">
              <Link
                to="/en/startup-journey"
                className="nav-mobile-link font-medium py-2 transition-colors duration-200"
                onClick={() => setIsMenuOpen(false)}
              >
                Startup Journey
              </Link>

              <Link
                to="/en/reports"
                className="nav-mobile-link font-medium py-2 transition-colors duration-200"
                onClick={() => setIsMenuOpen(false)}
              >
                Reports
              </Link>

              {/* Insight Lab Mobile */}
              <div className="py-2">
                <div className="nav-mobile-link font-medium mb-2">Insight Lab</div>
                <div className="pl-4 space-y-1">
                  <Link
                    to="/en/insightlab/what-we-do"
                    onClick={() => setIsMenuOpen(false)}
                    className="nav-mobile-link block text-sm py-1"
                  >
                    What we do
                  </Link>
                  <Link
                    to="/en/insightlab/consultation"
                    onClick={() => setIsMenuOpen(false)}
                    className="nav-mobile-link block text-sm py-1"
                  >
                    Consultation
                  </Link>
                  <Link
                    to="/en/insightlab/research"
                    onClick={() => setIsMenuOpen(false)}
                    className="nav-mobile-link block text-sm py-1"
                  >
                    Research
                  </Link>
                  <Link
                    to="/en/insightlab/contribute"
                    onClick={() => setIsMenuOpen(false)}
                    className="nav-mobile-link block text-sm py-1"
                  >
                    Contribute
                  </Link>
                  <Link
                    to="/en/calendar"
                    onClick={() => setIsMenuOpen(false)}
                    className="nav-mobile-link block text-sm py-1"
                  >
                    Calendar
                  </Link>
                </div>
              </div>

              {/* Startups Mobile */}
              <Link
                to="/en/startups"
                className="nav-mobile-link font-medium py-2 transition-colors duration-200"
                onClick={() => setIsMenuOpen(false)}
              >
                Startups
              </Link>

              {/* Scale-ups Mobile */}
              <Link
                to="/en/scaleups"
                className="nav-mobile-link font-medium py-2 transition-colors duration-200"
                onClick={() => setIsMenuOpen(false)}
              >
                Scale-ups
              </Link>

              {/* Engage Mobile */}
              <div className="py-2">
                <div className="nav-mobile-link font-medium mb-2">Engage</div>
                <div className="pl-4 space-y-1">
                  <Link
                    to="/en/engage/startup"
                    onClick={() => setIsMenuOpen(false)}
                    className="nav-mobile-link block text-sm py-1"
                  >
                    For Startups
                  </Link>
                  <Link
                    to="/en/engage/data"
                    onClick={() => setIsMenuOpen(false)}
                    className="nav-mobile-link block text-sm py-1"
                  >
                    For Data Contributors
                  </Link>
                  <Link
                    to="/en/engage/partner"
                    onClick={() => setIsMenuOpen(false)}
                    className="nav-mobile-link block text-sm py-1"
                  >
                    For Partnerships
                  </Link>
                </div>
              </div>

              {/* About Mobile */}
              <div className="py-2">
                <div className="nav-mobile-link font-medium mb-2">About</div>
                <div className="pl-4 space-y-1">
                  <Link
                    to="/en/about"
                    onClick={() => setIsMenuOpen(false)}
                    className="nav-mobile-link block text-sm py-1"
                  >
                    About SSN
                  </Link>
                  <Link
                    to="/en/newsroom"
                    onClick={() => setIsMenuOpen(false)}
                    className="nav-mobile-link block text-sm py-1"
                  >
                    News & Press
                  </Link>
                  <Link
                    to="/en/partners"
                    onClick={() => setIsMenuOpen(false)}
                    className="nav-mobile-link block text-sm py-1"
                  >
                    Partners
                  </Link>
                </div>
              </div>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export { HeaderEn };
export default HeaderEn;
